/**
 * Sensor coverage mask (screen space, RGBA8): R = inside any of our team's sensor discs
 * (crisp 2 px edge); A = the same for our own sensors only (the territory pass draws the
 * coverage outline at its 0.5 contour — allied coverage lights the fog but draws no line);
 * G = wide-area (radar-class) coverage plus a faint halo just outside it ("edge of
 * knowledge"); B = coverage that fades in over ~24 px inside the edge (soft fog boundary).
 * Small sensors (cities, SAMs) only count towards R and B.
 * Drawn as instanced quads with MAX blending; sampled by the territory and coast passes.
 */
import { GLSL_COMMON, makeProgram, makeTarget, freeTarget, bindTarget, type GL, type Program, type Target } from './gl';
import type { FrameParams } from './frame';

const VS = /* glsl */ `#version 300 es
precision highp float;
${GLSL_COMMON}
uniform float uDpr;
in vec2 aCorner;
in vec4 iDisc;   // x, y, r (mu), halo (0/1) + 2 × own (0/1)
out vec2 vRel;
flat out float vR;
flat out float vHalo;
flat out float vOwn;
void main() {
  float R = iDisc.z * uCam.z;
  vHalo = mod(iDisc.w, 2.0);
  vOwn = floor(iDisc.w * 0.5);
  float ext = R + 40.0 * uDpr;
  vec2 c = worldToPx(iDisc.xy);
  vec2 p = c + vec2(aCorner.x * 2.0 - 1.0, aCorner.y) * ext;
  vRel = p - c;
  vR = R;
  gl_Position = pxToClip(p);
}`;

const FS = /* glsl */ `#version 300 es
precision highp float;
uniform float uDpr;
in vec2 vRel;
flat in float vR;
flat in float vHalo;
flat in float vOwn;
out vec4 outColor;
void main() {
  float d = length(vRel);
  float cov = 1.0 - smoothstep(vR - 2.0 * uDpr, vR, d);
  float halo = (d > vR ? exp(-(d - vR) / (22.0 * uDpr)) : 1.0) * vHalo;
  float soft = 1.0 - smoothstep(vR - min(24.0 * uDpr, 0.5 * vR), vR, d);
  outColor = vec4(cov, halo, soft, cov * vOwn);
}`;

export class VisLayer {
  private prog: Program;
  private vao: WebGLVertexArrayObject;
  private buf: WebGLBuffer;
  private data = new Float32Array(4 * 256);
  private n = 0;
  private target: Target | null = null;

  constructor(private gl: GL) {
    this.prog = makeProgram(gl, 'vis', VS, FS, ['uView', 'uCam', 'uDpr']);
    const corners = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, corners);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, -1, 1, -1, 0, 1, 1, 1]), gl.STATIC_DRAW);
    this.buf = gl.createBuffer()!;
    this.vao = gl.createVertexArray()!;
    gl.bindVertexArray(this.vao);
    const lc = gl.getAttribLocation(this.prog.prog, 'aCorner');
    gl.enableVertexAttribArray(lc);
    gl.vertexAttribPointer(lc, 2, gl.FLOAT, false, 0, 0);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buf);
    const li = gl.getAttribLocation(this.prog.prog, 'iDisc');
    gl.enableVertexAttribArray(li);
    gl.vertexAttribPointer(li, 4, gl.FLOAT, false, 16, 0);
    gl.vertexAttribDivisor(li, 1);
    gl.bindVertexArray(null);
  }

  get texture(): WebGLTexture {
    return this.target!.tex;
  }

  clear(): void {
    this.n = 0;
  }

  /** Sensor disc; `halo` draws the faint edge-of-knowledge glow outside it (radars); `own`: ours (outlined). */
  add(x: number, y: number, r: number, halo: boolean, own = true): void {
    if ((this.n + 1) * 4 > this.data.length) {
      const next = new Float32Array(this.data.length * 2);
      next.set(this.data);
      this.data = next;
    }
    this.data.set([x, y, r, (halo ? 1 : 0) + (own ? 2 : 0)], this.n * 4);
    this.n++;
  }

  render(f: FrameParams): void {
    const gl = this.gl;
    if (!this.target || this.target.w !== f.viewW || this.target.h !== f.viewH) {
      freeTarget(gl, this.target);
      this.target = makeTarget(gl, f.viewW, f.viewH);
    }
    bindTarget(gl, this.target);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    if (this.n === 0) return;
    gl.enable(gl.BLEND);
    gl.blendEquation(gl.MAX);
    gl.useProgram(this.prog.prog);
    gl.uniform2f(this.prog.u.uView!, f.viewW, f.viewH);
    gl.uniform3f(this.prog.u.uCam!, f.camX, f.camY, f.zoomPx);
    gl.uniform1f(this.prog.u.uDpr!, f.dpr);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buf);
    gl.bufferData(gl.ARRAY_BUFFER, this.data.subarray(0, this.n * 4), gl.STREAM_DRAW);
    gl.bindVertexArray(this.vao);
    gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, this.n);
    gl.bindVertexArray(null);
    gl.blendEquation(gl.FUNC_ADD);
    gl.disable(gl.BLEND);
  }
}
